import { useEffect, useRef } from "react";

import { CircularProgress, Container } from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

const CallbackComponent = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const navigate = useNavigate();
  const isHandlingRef = useRef(false);

  useEffect(() => {
    // Prevent running if already authenticated (might have been handled already)
    if (authState?.isAuthenticated && !window.location.search) {
      console.log('[Callback] Already authenticated and no OAuth params, navigating to home');
      navigate({ to: "/", replace: true });
      return;
    }

    // Use ref to prevent double execution in React Strict Mode
    if (isHandlingRef.current) {
      console.log('[Callback] Already handling, skipping duplicate execution');
      return;
    }

    const handleCallback = async () => {
      isHandlingRef.current = true;

      try {
        // Check if this is actually a login redirect with OAuth parameters
        const urlParams = new URLSearchParams(window.location.search);
        const hasCode = urlParams.has('code');
        const hasError = urlParams.has('error');
        const hasState = urlParams.has('state');
        
        console.log('[Callback] URL params:', { 
          hasCode, 
          hasError, 
          hasState,
          pathname: window.location.pathname,
          search: window.location.search 
        });

        // Handle OAuth error responses first (before SDK processing).
        // The authorization server sends error= in the callback URL when the flow fails.
        // We handle these directly without invoking handleLoginRedirect() because the SDK
        // only throws OAuthError when BOTH error= AND error_description= are present.
        if (hasError) {
          const errorCode = urlParams.get('error');
          console.warn('[Callback] OAuth error in redirect:', errorCode);
          if (errorCode === 'access_denied') {
            navigate({ to: "/no-permission", replace: true });
          } else {
            navigate({ to: "/login", replace: true });
          }
          return;
        }

        // No OAuth params at all — not a valid callback, redirect home
        if (!hasCode) {
          console.log('[Callback] No OAuth parameters found, navigating to home');
          navigate({ to: "/", replace: true });
          return;
        }

        // Only call handleLoginRedirect if we have valid OAuth response
        if (oktaAuth.token.isLoginRedirect()) {
          // Store redirectUri before handleLoginRedirect clears it
          const originalUri = oktaAuth.getOriginalUri();
          
          console.log('[Callback] Handling login redirect, originalUri:', originalUri);

          await oktaAuth.handleLoginRedirect();
          
          console.log('[Callback] Login redirect handled successfully');
          
          // Navigate to original URI or fallback to home
          navigate({ 
            to: originalUri || "/", 
            replace: true 
          });
        } else {
          console.warn('[Callback] Has OAuth params but isLoginRedirect() is false');
          navigate({ to: "/", replace: true });
        }
      } catch (error) {
        console.error('[Callback] Login redirect failed:', error);
        
        // Check if it's a specific OAuth error
        if (error instanceof Error) {
          if (error.message.includes('Unable to parse')) {
            console.error('[Callback] OAuth parsing error - redirecting to login');
            navigate({ to: "/login", replace: true });
          } else if (error.message.includes('access_denied')) {
            console.error('[Callback] Access denied - redirecting to no-permission');
            navigate({ to: "/no-permission", replace: true });
          } else {
            console.error('[Callback] Unknown error - redirecting to login');
            navigate({ to: "/login", replace: true });
          }
        } else {
          navigate({ to: "/login", replace: true });
        }
      }
    };

    handleCallback();
    
    // Cleanup function to reset ref if component unmounts
    return () => {
      // Don't reset here - we want to prevent re-execution even on remount
    };
  }, [oktaAuth, authState, navigate]);

  return (
    <Container sx={{ textAlign: "center", p: 4 }}>
      <CircularProgress />
    </Container>
  );
};

export const Route = createFileRoute("/callback")({
  component: CallbackComponent,
});
